/**
 * logSystemEvent — fire-and-forget logging to system_events via edge function.
 * Used for admin observability. Never blocks UI.
 */

import { supabase } from '@/integrations/supabase/client';
import { normalizeEntityId } from '@/lib/observability/entityId';

export type ReasonKind = 'validation' | 'guard' | 'rls' | 'network' | 'server' | 'unknown';

export interface SystemEventPayload {
  level: 'info' | 'warn' | 'error';
  event_key: string;
  domain?: string;
  entity_type?: string;
  entity_id?: string;
  producer_id?: string;
  message: string;
  context?: Record<string, unknown>;
  request_id?: string;
}

export function generateRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Fire-and-forget. Never throws. Never blocks UI.
 * entity_id is normalized to uuid or null before sending.
 */
export async function logSystemEvent(payload: SystemEventPayload): Promise<void> {
  try {
    const safePayload = {
      ...payload,
      entity_id: normalizeEntityId(payload.entity_id),
    };
    await supabase.functions.invoke('log-system-event', {
      body: safePayload,
    });
  } catch {
    // Intentionally swallowed — observability must never break UX
    if (process.env.NODE_ENV === 'development') {
      console.warn('[logSystemEvent] failed to send', payload.event_key);
    }
  }
}
